import { Loader } from '@googlemaps/js-api-loader'
import OLLayerGroup from 'ol/layer/Group'
import { transform as OLProjTransform } from 'ol/proj'

import type { Coordinate as OLCoordinate } from 'ol/coordinate'
import type { Options as OLLayerGroupOptions } from 'ol/layer/Group'
import type { ObjectEvent } from 'ol/Object'
import type OLMapView from 'ol/View'

import * as Model from 'api/model'
import type { ConfigLayerOptions } from 'config/ConfigLayer'
import { MapLayer } from 'config/MapLayer'
import { setLayerLoadDone, setLayerLoadFailed, setLayerLoadInProgress } from 'drawer/tabs/layers/actions'
import { getMap } from 'utils/MapUtils'
import PoziNotifications from 'utils/notifications'

const getMapView = () => {
  const mapView = getMap()?.getView()

  return mapView
}

export class MapLayerGoogleMaps extends MapLayer {
  static createLayer (layerConf: ConfigLayerOptions): OLLayerGroup {
    this.preLayer(layerConf)

    const { group, groupid, title, titleid } = layerConf

    let gmap: google.maps.Map
    let olMapView: OLMapView

    console.debug('MapLayerGoogleMaps - createLayer', titleid)

    const options: OLLayerGroupOptions = {
      visible: false
    }

    const olLayer = new OLLayerGroup(options)

    // Set some useful properties to the layer
    olLayer.setProperties({
      title,
      titleid,
      group,
      groupid,
      initialised: false // custom property to determine if we have initialised this layer
    })

    const apiKey = layerConf.config?.google?.key
    // const apiKey = layerConf.config?.google?.key_development // when developing, use unrestricted key
    if (!apiKey) {
      console.log('MapLayerGoogle - API key not provided!', titleid, layerConf.config?.google)
      return olLayer
    }

    // The mapId can be provided for a specific map (style), e.g. a 'clean' map without commercial POIs
    const mapId = layerConf.config?.google?.mapId

    // Listen to layer changes (like opacity and visible)
    const onLayerChange = (evt: ObjectEvent) => {
      const { key, target: olLayer } = evt

      // console.debug('MapLayerGoogle - onLayerChange', key, olLayer, { ...evt })

      const gmapEl = document.getElementById('gmap')
      if (!gmapEl) {
        console.log('MapLayerGoogle - onLayerChange - gmap element does not exist')

        return
      }

      switch (key) {
        case 'opacity': {
          const opacity = olLayer.getOpacity()
          gmapEl.style.opacity = opacity
          break
        }
        case 'visible': {
          const visible = olLayer.getVisible()
          gmapEl.style.visibility = visible ? 'visible' : 'hidden'

          if (!olMapView) {
            olMapView = getMapView()
          }

          if (!olMapView) {
            console.log('MapLayerGoogle - onLayerChange - olMapView does not exist')

            return
          }

          // Enable/disable syncing with Google Maps
          if (visible) {
            olMapView.on(['change:center', 'change:resolution'], syncMapWithGoogleMaps)

            syncMapWithGoogleMaps() // Make sure that the map is in sync (again)
          } else {
            olMapView.un(['change:center', 'change:resolution'], syncMapWithGoogleMaps)
          }
          break
        }
      }
    }

    const syncMapWithGoogleMaps = () => {
      if (!gmap) {
        console.log('MapLayerGoogle - syncMapWithGoogleMaps - gmap does not exist (anymore?)')

        return
      }

      if (!olMapView) {
        olMapView = getMapView()
      }

      const center = OLProjTransform(olMapView.getCenter() as OLCoordinate, 'EPSG:3857', 'EPSG:4326')
      const zoom = olMapView.getZoom()

      const [lng, lat] = center

      // console.debug('MapLayerGoogle - syncMapWithGoogleMaps', center, lat, lng)

      gmap.moveCamera({
        center: {
          lat,
          lng
        },
        zoom
      })
    }

    const loadLibrary = (titleid: string, apiKey: string, mapId?: string) => {
      // console.debug('MapLayerGoogle - loadLibrary - apiKey', titleid, apiKey, mapId)

      if (!olMapView) {
        olMapView = getMapView()
      }

      window.store.dispatch(setLayerLoadInProgress(titleid))

      // Create Google Maps loader and initialise map
      const loader = new Loader({
        apiKey
      })

      loader.importLibrary('maps').then(({ Map }) => {
        // console.debug('MapLayerGoogle - loadLibrary - loaded', Map)

        const gmapEl = document.getElementById('gmap')
        if (!gmapEl) {
          console.log('MapLayerGoogle - loadLibrary - gmap element does not exist')

          window.store.dispatch(setLayerLoadFailed(titleid))

          return
        }

        const gmapOptions = {
          disableDefaultUI: true,
          keyboardShortcuts: false,
          draggable: false,
          disableDoubleClickZoom: true,
          mapId,
          scrollwheel: false,
          streetViewControl: false
        }

        gmap = new Map(gmapEl, gmapOptions)

        // console.debug('MapLayerGoogle - loadLibrary - gmap', gmap)

        if (!gmap) {
          console.log('MapLayerGoogle - loadLibrary - failed to create gmap', gmap)
          return
        }

        // TODO (maybe): disable / enable the listeners depending on whether layer is active or not
        olMapView.on(['change:center', 'change:resolution'], syncMapWithGoogleMaps)

        syncMapWithGoogleMaps()

        window.store.dispatch(setLayerLoadDone(titleid))
      }).catch((error) => {
        console.log('MapLayerGoogle - loadLibrary - error', titleid, error)

        PoziNotifications().warning('Something went wrong: unable to load Google Maps layer')

        window.store.dispatch(setLayerLoadFailed(titleid))
      })
    }

    // A bit of a convoluted way to detect the first time the layer is visible to
    // trigger the loading of the library. This makes sure that the layer only
    // gets loaded when needed (avoids unneccessary data fetching)
    const onLayerFirstTimeVisible = () => {
      // console.debug('MapLayerGoogle - onLayerFirstTimeVisible', olLayer, { ...evt })

      const olMapView = getMapView()

      if (!olMapView) {
        console.info('MapLayerGoogle - Map is not ready yet. Waiting a bit longer.', olLayer.get('titleid'))

        // Listen once more for visibility change.
        olLayer.once('change:visible', onLayerFirstTimeVisible)

        return
      }

      const isInitialised = olLayer.get('initialised')
      if (isInitialised) {
        console.info('MapLayerGoogle - layer already initialised. We should never get here.', olLayer.get('titleid'))
        return
      }

      const isVisible = olLayer.getVisible()
      if (isVisible) {
        olLayer.set('initialised', true)

        // Synchronise gmap visibility with OL layer visibility
        olLayer.on('change:opacity', onLayerChange)
        olLayer.on('change:visible', onLayerChange)

        // Trigger loading of Google Maps library
        const titleid = olLayer.get('titleid')

        loadLibrary(titleid, apiKey, mapId)
      } else {
        console.info('MapLayerGoogle - layer not visible yet. We should never get here.', olLayer.get('titleid'))

        // Listen once more for visibility change. Should be visible next time the visibility toggles
        olLayer.once('change:visible', onLayerFirstTimeVisible)
      }
    }

    // Only start processing layers when the OL map has become available
    // Note: this assumes that at this point in time the OL map has not been initialised
    const mapListener = Model.on('app.map.olMap', () => {
      Model.un('app.map.olMap', mapListener) // stop listening (since we only want to do this once)

      // Process Google Maps layers when first first visible, either immediately or on next visibility change
      const isVisible = olLayer.getVisible()
      if (isVisible) {
        onLayerFirstTimeVisible()
      } else {
        olLayer.once('change:visible', onLayerFirstTimeVisible)
      }
    })

    // console.debug('MapLayerGoogle', titleid, layerConf, olLayer)

    return olLayer
  }
}
